import { FunctionalComponent } from 'preact'
import { useState, useRef, useEffect } from 'preact/hooks'
import { Sparkles, Loader2, CheckCircle2, AlertCircle, Clock, AlertTriangle, FileText } from 'lucide-preact'
import { leadsApi } from '@/api/leads'
import { audienceApi } from '@/api/audience'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface EnrichmentStatusCellProps {
  enrichmentStatus?: string | null
  enrichedAt?: string | null
  enrichmentAvailableDate?: string | null
  enrichmentAttemptCount: number
  /** The lead's email. Read only when context='lead': the API refuses to re-enrich a lead without one. */
  email?: string | null
  /** ID of the record - either leadId (when context='lead') or fileImportRowId (when context='fileImportRow') */
  id: string
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  /** Context determines which API to call: 'lead' uses leads API, 'fileImportRow' uses audience API */
  context?: 'lead' | 'fileImportRow'
  onEnrichmentUpdated?: (updated: {
    EnrichmentStatus?: string | null
    EnrichedAt?: string | null
    EnrichmentAvailableDate?: string | null
    EnrichmentAttemptCount: number
  }) => void
}

/**
 * Reusable component for displaying enrichment status with retry capabilities.
 * Shows enrichment state and allows retry for failed enrichments.
 */
export const EnrichmentStatusCell: FunctionalComponent<EnrichmentStatusCellProps> = ({
  enrichmentStatus,
  enrichedAt,
  enrichmentAvailableDate,
  enrichmentAttemptCount,
  email,
  id,
  clientCompanyId,
  workspaceId,
  campaignId,
  context = 'lead',
  onEnrichmentUpdated
}) => {
  const [isEnriching, setIsEnriching] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { showSuccess, showError } = useToast()
  const { canEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const canEnrich = !enrichmentAvailableDate || new Date(enrichmentAvailableDate) <= new Date()
  // The API refuses to re-enrich a lead without an email, so such a lead is never offered Retry or
  // Re-enrich, nor told to wait for one.
  const isReEnrichable = context === 'fileImportRow' || !!email
  // A row the file gave us with no LinkedIn URN. Complete, but nothing was looked up, so it gets
  // neither the green check nor an Enrich button — the button would queue an orchestration that
  // cannot do person enrichment and would land the row right back here.
  const isImported = enrichmentStatus === 'Imported'
  const isFailed = enrichmentStatus === 'Failed'
  const isSuccess = enrichmentStatus === 'Success'
  const isPending = enrichmentStatus === 'Pending'
  const isNeedsReview = enrichmentStatus === 'NeedsReview'
  const isNotStarted = enrichmentStatus === 'NotStarted' || !enrichmentStatus


  const handleEnrich = async (e: MouseEvent) => {
    e.stopPropagation()
    if (!canEnrich || isEnriching) return

    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsEnriching(true)

    // First, optimistically update the status to show enriching state immediately
    onEnrichmentUpdated?.({
      EnrichmentStatus: 'Pending',
      EnrichedAt: null,
      EnrichmentAvailableDate: null,
      EnrichmentAttemptCount: enrichmentAttemptCount + 1
    })

    try {
      console.log('[EnrichmentStatusCell] Re-enrich:', {
        context,
        workspaceId,
        campaignId,
        id,
        url: context === 'fileImportRow'
          ? `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/FileImportRows/${id}/re-enrich`
          : `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Leads/${id}/re-enrich`
      })
      const resp = context === 'fileImportRow'
            ? await audienceApi.reEnrich(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
            : await leadsApi.reEnrich(workspaceId, campaignId, id, abortController.signal)

      // API returns 202 Accepted - enrichment is queued
      if (resp.status === 202) {
        showSuccess('Enrichment queued', 'Contact data enrichment has been requested')
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      // Revert status on error
      onEnrichmentUpdated?.({
        EnrichmentStatus: 'Failed',
        EnrichedAt: null,
        EnrichmentAvailableDate: null,
        EnrichmentAttemptCount: enrichmentAttemptCount
      })
      showError('Failed to queue enrichment', 'Please try again')
    } finally {
      if (!abortController.signal.aborted) {
        setIsEnriching(false)
      }
    }
  }

  // Show nothing if not started and no attempts (will be auto-queued)
  if (isNotStarted && enrichmentAttemptCount === 0) {
    return <span class="text-slate-400 dark:text-slate-500">—</span>
  }

  if (isImported) {
    return (
      <div
        class="flex items-center gap-1 text-slate-500 dark:text-slate-400"
        title="Imported from the file. No LinkedIn URN, so no profile was looked up."
      >
        <FileText class="w-4 h-4" />
        <span class="text-xs">Imported</span>
      </div>
    )
  }

  return (
    <div class="flex items-center gap-2">
      {isEnriching && (
        <div class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
          <Loader2 class="w-4 h-4 animate-spin" />
          <span class="text-xs">Enriching...</span>
        </div>
      )}

      {!isEnriching && isSuccess && (
        <div
          class="flex items-center gap-1 text-green-600 dark:text-green-400"
          title={enrichedAt ? `Enriched on ${new Date(enrichedAt).toLocaleString()}` : 'Successfully enriched'}
        >
          <CheckCircle2 class="w-4 h-4" />
          <span class="text-xs">Enriched</span>
        </div>
      )}

      {!isEnriching && isFailed && (
        <div class="flex items-center gap-1">
          <div
            class="flex items-center gap-1 text-red-600 dark:text-red-400"
            title={`Failed (${enrichmentAttemptCount} attempt${enrichmentAttemptCount !== 1 ? 's' : ''})`}
          >
            <AlertCircle class="w-4 h-4" />
            <span class="text-xs">Failed</span>
          </div>
          {isReEnrichable && canEnrich && canEdit && (
            <button
              onClick={handleEnrich}
              class="px-2 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              title="Retry enrichment"
            >
              Retry
            </button>
          )}
          {isReEnrichable && !canEnrich && enrichmentAvailableDate && (
            <span
              class="text-xs text-slate-500 dark:text-slate-400"
              title={`Available ${new Date(enrichmentAvailableDate).toLocaleString()}`}
            >
              (wait)
            </span>
          )}
        </div>
      )}

      {!isEnriching && isPending && canEnrich && (
        <div
          class="flex items-center gap-1 text-blue-600 dark:text-blue-400"
          title="Enrichment in progress"
        >
          <Sparkles class="w-4 h-4" />
          <span class="text-xs">Enriching...</span>
        </div>
      )}

      {!isEnriching && isPending && !canEnrich && enrichmentAvailableDate && (
        <div
          class="flex items-center gap-1 text-slate-600 dark:text-slate-400"
          title={`Available ${new Date(enrichmentAvailableDate).toLocaleString()}`}
        >
          <Clock class="w-4 h-4" />
          <span class="text-xs">Pending</span>
        </div>
      )}

      {!isEnriching && isNeedsReview && (() => {

        return (
          <div class="flex items-center gap-1">
            <div
              class="flex items-center gap-1 text-amber-600 dark:text-amber-400"
              title="Enrichment needs review"
            >
              <AlertTriangle class="w-4 h-4" />
              <span class="text-xs">Needs Review</span>
            </div>
          {isReEnrichable && canEnrich && canEdit && (
            <button
              onClick={handleEnrich}
              class="px-2 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              title="Re-enrich contact"
            >
              Re-enrich
            </button>
          )}
        </div>
        )
      })()}
    </div>
  )
}
